import { useOrganization, useUser } from "@clerk/clerk-react";
import { Alert, Button, Spinner, Typography } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { OnboardingForm } from "@/components/auth/onboarding/form";
import { AuthSplitShell } from "@/components/auth/split-shell";
import { ORGANIZATION_NAME_FIELD } from "@/constants/organization";
import { extractErrorMessage } from "@/utils/error";
import { useOpenOrganization } from "@/hooks/use-open-organization";

export const MissingOrganization = () => {
  const { user } = useUser();
  const { organization } = useOrganization();
  const openOrganization = useOpenOrganization();
  const navigate = useNavigate();
  const { mutate, isPending, variables, isError, error, reset } = useMutation({
    mutationFn: openOrganization,
  });

  const { noun } = ORGANIZATION_NAME_FIELD;
  const missingName = organization?.name ?? `Your ${noun}`;
  const otherOrganizations = (user?.organizationMemberships ?? [])
    .map((membership) => membership.organization)
    .filter(({ id }) => id !== organization?.id);
  const hasOtherOrganizations = otherOrganizations.length > 0;
  const openingId = isPending ? variables : undefined;

  const handleOpen = (organizationId: string) => {
    reset();
    mutate(organizationId);
  };

  return (
    <AuthSplitShell
      contentClassName="max-w-md"
      description={`${missingName} isn't set up here. Open another ${noun} or create a new one.`}
      footer={
        <Button
          className="self-center text-muted"
          size="sm"
          variant="ghost"
          onPress={() => navigate("/auth/sign-out")}
        >
          <Icon className="text-base" icon="solar:logout-2-outline" />
          <Typography type="body-sm">Log out</Typography>
        </Button>
      }
      title={`Choose your ${noun}`}
    >
      {isError && (
        <Alert status="danger">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>{extractErrorMessage(error)}</Alert.Title>
          </Alert.Content>
        </Alert>
      )}

      {hasOtherOrganizations && (
        <div className="flex flex-col gap-2">
          {otherOrganizations.map(({ id, name }) => (
            <Button
              key={id}
              className="w-full justify-between"
              isDisabled={isPending}
              isPending={openingId === id}
              variant="secondary"
              onPress={() => handleOpen(id)}
            >
              {({ isPending: isOpening }) => (
                <>
                  <Typography
                    className="text-current"
                    type="body-sm"
                    weight="medium"
                  >
                    {name}
                  </Typography>
                  {isOpening ? (
                    <Spinner color="current" size="sm" />
                  ) : (
                    <Icon icon="solar:alt-arrow-right-linear" width={18} />
                  )}
                </>
              )}
            </Button>
          ))}
        </div>
      )}

      {hasOtherOrganizations && (
        <Typography className="pt-2 text-muted" type="body-sm">
          Or create a new {noun}
        </Typography>
      )}

      <OnboardingForm />
    </AuthSplitShell>
  );
};
